import { useCallback, useMemo, useState } from 'react';
import { useSessionStorage } from 'react-use';
import { connect, useDispatch } from 'react-redux';
import { Form, FormRenderProps } from 'react-final-form';
import { FormState } from 'final-form';
import type { Dispatch } from 'redux';

import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import {
  makePasswordUppercaseRequiredValidation,
  makePasswordCyrillicValidation,
  makePasswordLowercaseRequiredValidation,
  makePasswordNumberRequiredValidation,
  makePasswordSpecialRequiredValidation,
  makeSameValidation,
  makeNotSameValidation,
} from 'CorePackage/MainModule/validators/isPasswordValid';
import { IS_ESIA } from 'CorePackage/MainModule/constants/localStorageNames';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import { makeMinCharactersValidator } from 'CorePackage/MainModule/validators/minCharacters';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './UserSettings.module.scss';
import { personalAccountUpdatePassword } from '../actions/personalAccountAction';

interface INewPasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const initialValues: INewPasswordForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

interface IUserSettingsStoreProps {
  isSupervisor: boolean;
}

const UserSettingsWithoutStore = ({ isSupervisor }: IUserSettingsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [fieldValues, setFieldValues] = useState<INewPasswordForm>(initialValues);
  const [isEsia] = useSessionStorage<boolean>(IS_ESIA);

  const wrapperAttributes = useMemo(
    () => ({
      ...(isEsia && { 'data-big': true }),
    }),
    [isEsia],
  );

  const onFormSubmit = useCallback((values: INewPasswordForm) => dispatch(personalAccountUpdatePassword(values)), [
    dispatch,
  ]);

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<INewPasswordForm>) => (
      <form className={styles.Wrapper} onSubmit={handleSubmit} {...wrapperAttributes}>
        <div className="shared-section-title">Смена пароля</div>
        {isEsia || isSupervisor ? (
          <div>Изменение пароля осуществляется в Единой системе идентификации и аутентификации (ЕСИА)</div>
        ) : (
          <>
            <Row>
              <FieldBlock label="Текущий пароль" color="black" placementLabel="left">
                <TextInputField
                  isPassword
                  name="currentPassword"
                  className="shared-row-field"
                  type="password"
                  validate={composeValidators(
                    makeRequired('Поле не может быть пустым'),
                    makeMaxCharactersValidator(256, 'Превышена максимальная длина'),
                  )}
                />
              </FieldBlock>
            </Row>
            <Row>
              <FieldBlock label="Новый пароль" color="black" placementLabel="left">
                <TextInputField
                  isPassword
                  name="newPassword"
                  type="password"
                  className="shared-row-field"
                  validate={composeValidators(
                    makeRequired('Поле не может быть пустым'),
                    makeMinCharactersValidator(8, 'Новый пароль должен содержать не менее 8 символов.'),
                    makeMaxCharactersValidator(256, 'Превышена допустимая длина строки.'),
                    makePasswordCyrillicValidation(
                      'Пароль должен содержать заглавные и прописные латинские буквы, цифры (0-9) и спецсимволы (!"#$%&*\'()+,-./:;<=>?@[]^_{|}~`).',
                    ),
                    makePasswordUppercaseRequiredValidation(
                      'Пароль должен содержать не менее одного заглавного латинского символа (A-Z).',
                    ),
                    makePasswordLowercaseRequiredValidation(
                      'Пароль должен содержать не менее одного прописного латинского символа (a-z).',
                    ),
                    makePasswordNumberRequiredValidation('Новый пароль должен содержать не менее одной цифры (0-9).'),
                    makePasswordSpecialRequiredValidation(
                      'Новый пароль должен содержать не менее одного спецсимвола (!”#$%&*,-./:;<=>?@[]^_{|}~’).',
                    ),
                    makeNotSameValidation(fieldValues.currentPassword, 'Новый пароль совпадает с текущим паролем.'),
                  )}
                />
              </FieldBlock>
            </Row>
            <Row>
              <FieldBlock label="Подтверждение нового пароля" color="black" placementLabel="left">
                <TextInputField
                  isPassword
                  name="confirmPassword"
                  type="password"
                  className="shared-row-field"
                  validate={composeValidators(
                    makeRequired('Поле не может быть пустым'),
                    makeSameValidation(fieldValues.newPassword, 'Подтверждение нового пароля введено неправильно.'),
                    makeMaxCharactersValidator(256, 'Превышена максимальная длина'),
                  )}
                />
              </FieldBlock>
            </Row>
            <Button
              className={styles.SaveButton}
              variant="contained"
              color="primary"
              type="submit"
              text="Сохранить"
              id="saveButton"
            />
          </>
        )}
      </form>
    ),
    [fieldValues.currentPassword, fieldValues.newPassword, isEsia, isSupervisor, wrapperAttributes],
  );

  const debugToState = useCallback((state: FormState<INewPasswordForm>) => setFieldValues(state.values), []);

  return (
    <Form<INewPasswordForm>
      debug={debugToState}
      onSubmit={onFormSubmit}
      render={renderForm}
      initialValues={initialValues}
    />
  );
};

const mapStateToProps = (state: IRootState): IUserSettingsStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
});

export const UserSettings = connect(mapStateToProps)(UserSettingsWithoutStore);
